import { Route } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { RoleGuard } from './core/auth/guards/role.guard';
import { AdminGuard } from './core/auth/guards/admin.guard';
import { RenterGuard } from './core/auth/guards/renter.guard';
import { SignOutResolver } from './core/auth/sign-out.resolver';
import { LayoutComponent } from './layout/layout.component';
import { UserLayoutComponent } from './layout/user-layout.component';
import { SignInComponent } from './modules/auth/sign-in/sign-in.component';
import { InitialDataResolver } from './app.resolvers';
import { UserMonitorComponent } from './modules/users/user-monitor/user-monitor.component';
import { MonitorAdsComponent } from './modules/public/monitor-ads/monitor-ads.component';
import { DigitalCardComponent } from './modules/public/digital-card/digital-card.component';

export const appRoutes: Route[] = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },

    {
        path: 'sign-in',
        component: SignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'sign-in/:username',
        component: SignInComponent,
        canActivate: [NoAuthGuard],
    },
    {
        path: 'bnb/:code',
        component: DigitalCardComponent,
    },
    {
        path: 'card/:tenantCode/:code',
        component: DigitalCardComponent,
    },
    {
        path: 'kartu/:tenantCode/:code',
        component: DigitalCardComponent,
    },

    {
        path: 'sign-out',
        canActivate: [AuthGuard],
        resolve: { action: SignOutResolver },
        children: [],
    },

    // ══════ Admin Layout (Sidebar + Topbar) ══════
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard, AdminGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'dashboard',
                loadChildren: () => import('./modules/admin/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'manajemen-tenant',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['occupant.read'] },
                loadChildren: () => import('./modules/admin/manajemen-tenant/manajemen-tenant.module').then(m => m.ManajemenTenantModule)
            },
            {
                path: 'manajemen-kamar',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['room.read'] },
                loadChildren: () => import('./modules/admin/manajemen-kamar/manajemen-kamar.module').then(m => m.ManajemenKamarModule)
            },
            {
                path: 'booking',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['order.read'] },
                loadChildren: () => import('./modules/admin/booking/booking.module').then(m => m.BookingModule)
            },
            {
                path: 'fasilitas-umum',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['facility-addon.read'] },
                loadChildren: () => import('./modules/admin/fasilitas-umum/fasilitas-umum.module').then(m => m.FasilitasUmumModule)
            },
            {
                path: 'kontrak-sewa',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['rental-contract.read'] },
                loadChildren: () => import('./modules/admin/kontrak-sewa/kontrak-sewa.module').then(m => m.KontrakSewaModule)
            },
            {
                path: 'manajemen-fasilitas',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['facility-category.read'] },
                loadChildren: () => import('./modules/admin/manajemen-fasilitas/manajemen-fasilitas.module').then(m => m.ManajemenFasilitasModule)
            },
            {
                path: 'manajemen-pelanggaran',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['violation.read', 'setting.read'] },
                loadChildren: () => import('./modules/admin/manajemen-pelanggaran/manajemen-pelanggaran.module').then(m => m.ManajemenPelanggaranModule)
            },
            {
                path: 'manajemen-pengaduan',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['complaint.read'] },
                loadChildren: () => import('./modules/admin/manajemen-pengaduan/manajemen-pengaduan.module').then(m => m.ManajemenPengaduanModule)
            },
            {
                path: 'tugas-harian',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['task.read'] },
                loadChildren: () => import('./modules/admin/tugas-harian/tugas-harian.module').then(m => m.TugasHarianModule)
            },
            {
                path: 'manajemen-task',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['task.read'] },
                loadChildren: () => import('./modules/admin/manajemen-task/manajemen-task.module').then(m => m.ManajemenTaskModule)
            },
            {
                path: 'manajemen-iklan',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['setting.read'] },
                loadChildren: () => import('./modules/admin/manajemen-iklan/manajemen-iklan.module').then(m => m.ManajemenIklanModule)
            },
            {
                path: 'profile',
                loadChildren: () => import('./modules/admin/profile/profile.module').then(m => m.ProfileModule)
            },
            {
                path: 'settings',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['setting.read'] },
                loadChildren: () => import('./modules/admin/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'setting-pembagian-hasil',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['setting.read'] },
                loadChildren: () => import('./modules/admin/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'payment-settings',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['setting.read'] },
                loadChildren: () => import('./modules/admin/payment-settings/payment-settings.module').then(m => m.PaymentSettingsModule)
            },
            {
                path: 'cctv',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['cctv.read'] },
                loadChildren: () => import('./modules/admin/cctv/cctv.module').then(m => m.CctvModule)
            },
            {
                path: 'laporan',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner'], permissions: ['room_report.read', 'payment.read', 'rental-contract.read'] },
                loadChildren: () => import('./modules/admin/laporan/laporan.module').then(m => m.LaporanModule)
            },
            {
                path: 'owner-disbursements',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner'], permissions: ['owner-disbursement.read'] },
                loadChildren: () => import('./modules/admin/owner-disbursements/owner-disbursements.module').then(m => m.OwnerDisbursementsModule)
            },
            {
                path: 'asset-purchases',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner'], permissions: ['asset-purchase.read'] },
                loadChildren: () => import('./modules/admin/asset-purchases/asset-purchases.module').then(m => m.AssetPurchasesModule)
            },
            {
                path: 'list-harga',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['room_type.read'] },
                loadChildren: () => import('./modules/admin/list-harga/list-harga.module').then(m => m.ListHargaModule)
            },
            {
                path: 'voucher',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['voucher.read'] },
                loadChildren: () => import('./modules/admin/voucher/voucher.module').then(m => m.VoucherModule)
            },
            {
                path: 'log-activity',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner', 'keeper'], permissions: ['audit.read'] },
                loadChildren: () => import('./modules/admin/log-activity/log-activity.module').then(m => m.LogActivityModule)
            },
            {
                path: 'manajemen-user',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner'], permissions: ['user.read'] },
                loadChildren: () => import('./modules/admin/manajemen-user/manajemen-user.module').then(m => m.ManajemenUserModule)
            },
            {
                path: 'manajemen-role',
                canActivate: [RoleGuard],
                data: { roles: ['administrator', 'owner'], permissions: ['role.read'] },
                loadChildren: () => import('./modules/admin/manajemen-role/manajemen-role.module').then(m => m.ManajemenRoleModule)
            },
            {
                path: 'manajemen-permission',
                canActivate: [RoleGuard],
                data: { roles: ['administrator'], permissions: ['permission.read'] },
                loadChildren: () => import('./modules/admin/manajemen-permission/manajemen-permission.module').then(m => m.ManajemenPermissionModule)
            },
            {
                path: 'daftar-tenant',
                canActivate: [RoleGuard],
                data: { roles: ['administrator'], permissions: ['tenant.read', 'permission.read'] },
                loadChildren: () => import('./modules/admin/daftar-tenant/daftar-tenant.module').then(m => m.DaftarTenantModule)
            },
            {
                path: 'test-payment',
                loadChildren: () => import('./modules/admin/test-payment/test-payment.module').then(m => m.TestPaymentModule)
            },
        ]
    },

    // ══════ User Layout (Mobile, No Sidebar) ══════
    {
        path: 'user',
        component: UserLayoutComponent,
        canActivate: [AuthGuard, RenterGuard],
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('./modules/users/user-home/user-home.module').then(m => m.UserHomeModule)
            },
            {
                path: 'notifications',
                loadChildren: () => import('./modules/users/user-notifications/user-notifications.module').then(m => m.UserNotificationsModule)
            },
            {
                path: 'pengaduan',
                loadChildren: () => import('./modules/users/user-pengaduan/user-pengaduan.module').then(m => m.UserPengaduanModule)
            },
            {
                path: 'maintenance',
                loadChildren: () => import('./modules/users/user-maintenance/user-maintenance.module').then(m => m.UserMaintenanceModule)
            },
            {
                path: 'contract',
                loadChildren: () => import('./modules/users/user-contract/user-contract.module').then(m => m.UserContractModule)
            },
            {
                path: 'pelanggaran',
                loadChildren: () => import('./modules/users/user-pelanggaran/user-pelanggaran.module').then(m => m.UserPelanggaranModule)
            },
            {
                path: 'info',
                loadChildren: () => import('./modules/users/user-info/user-info.module').then(m => m.UserInfoModule)
            },
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full'
            },
        ]
    },
    {
        path: 'monitor/:tenantCode/:roomId',
        component: UserMonitorComponent,
    },
    {
        path: 'monitor',
        component: UserMonitorComponent,
    },
    {
        path: 'monitor-ads/:tenantCode/:slug',
        component: MonitorAdsComponent,
    },

    {
        path: ':tenantCode/:code',
        component: DigitalCardComponent,
    },

    // Fallback 404
    {
        path: '**',
        redirectTo: 'sign-in',
    },
];

